import React, { useState, useEffect, useCallback } from 'react';
import { projectsAPI } from '../../../services/api';
import { TASK_STATUSES, PRIORITIES, STATUS_LABEL, PRIORITY_COLOUR, errMsg } from '../constants';

const fmtDate = (s) => (s ? s.slice(0, 10) : '');

const TaskModal = ({ taskId, users, onClose, onChanged }) => {
  const [task, setTask] = useState(null);
  const [error, setError] = useState(null);
  const [comment, setComment] = useState('');
  const [subtask, setSubtask] = useState('');
  const [uploading, setUploading] = useState(false);

  const userName = useCallback((id) => {
    const u = users.find((x) => x.id === id);
    return u ? (u.full_name || u.email) : '—';
  }, [users]);

  const load = useCallback(async () => {
    try {
      setTask(await projectsAPI.getTaskDetail(taskId));
    } catch (err) {
      setError(errMsg(err, 'Failed to load task'));
    }
  }, [taskId]);

  useEffect(() => { load(); }, [load]);

  const patch = async (obj) => {
    try {
      await projectsAPI.updateTask(taskId, { title: task.title, ...obj });
      await load();
      onChanged && onChanged();
    } catch (err) {
      setError(errMsg(err, 'Failed to save'));
    }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    await projectsAPI.addComment({ task_id: taskId, body: comment.trim() });
    setComment('');
    await load();
  };

  const addSubtask = async () => {
    if (!subtask.trim() || !task) return;
    await projectsAPI.createTask(task.project_id, { title: subtask.trim(), parent_task_id: taskId });
    setSubtask('');
    await load();
    onChanged && onChanged();
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await projectsAPI.uploadResource(file, { task_id: taskId, title: file.name });
      await load();
    } catch (err) {
      setError(errMsg(err, 'Upload failed'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const download = async (id) => {
    const { url } = await projectsAPI.resourceDownloadUrl(id);
    if (url) window.open(url, '_blank', 'noopener');
  };

  const remove = async () => {
    if (!window.confirm('Delete this task?')) return;
    await projectsAPI.deleteTask(taskId);
    onChanged && onChanged();
    onClose();
  };

  if (!task) {
    return (
      <div className="proj-modal-overlay" onClick={onClose}>
        <div className="proj-modal" onClick={(e) => e.stopPropagation()}><div className="proj-modal-body">Loading…</div></div>
      </div>
    );
  }

  return (
    <div className="proj-modal-overlay" onClick={onClose}>
      <div className="proj-modal proj-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="proj-modal-header">
          <input className="proj-task-title-input" defaultValue={task.title}
            onBlur={(e) => e.target.value.trim() && e.target.value !== task.title && patch({ title: e.target.value.trim() })} />
          <button onClick={onClose}>×</button>
        </div>
        {error && <div className="proj-alert proj-alert-danger">{error}<button onClick={() => setError(null)}>×</button></div>}
        <div className="proj-modal-body">
          <div className="proj-task-fields">
            <label className="proj-field"><span>Status</span>
              <select className="proj-select" value={task.status} onChange={(e) => patch({ status: e.target.value })}>
                {TASK_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
              </select>
            </label>
            <label className="proj-field"><span>Priority</span>
              <select className="proj-select" value={task.priority} onChange={(e) => patch({ priority: e.target.value })}>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            <label className="proj-field"><span>Assignee</span>
              <select className="proj-select" value={task.assignee_id || ''} onChange={(e) => patch({ assignee_id: e.target.value ? parseInt(e.target.value, 10) : null })}>
                <option value="">— Unassigned —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
              </select>
            </label>
            <label className="proj-field"><span>Manager</span>
              <select className="proj-select" value={task.manager_id || ''} onChange={(e) => patch({ manager_id: e.target.value ? parseInt(e.target.value, 10) : null })}>
                <option value="">— None —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
              </select>
            </label>
            <label className="proj-field"><span>Due date</span>
              <input type="date" className="proj-input" defaultValue={fmtDate(task.due_date)} onChange={(e) => patch({ due_date: e.target.value || null })} />
            </label>
          </div>

          <label className="proj-field proj-meta-desc"><span>Description</span>
            <textarea className="proj-input" rows={3} defaultValue={task.description || ''}
              onBlur={(e) => e.target.value !== (task.description || '') && patch({ description: e.target.value })} />
          </label>

          <div className="proj-task-actions">
            {task.status !== 'done'
              ? <button className="proj-btn proj-btn-primary" onClick={() => projectsAPI.completeTask(taskId).then(() => { load(); onChanged && onChanged(); })}>Mark complete</button>
              : <span className="proj-done-flag">✓ Completed</span>}
            <button className="proj-btn proj-btn-danger" onClick={remove}>Delete task</button>
          </div>

          {/* Subtasks */}
          <div className="proj-section">
            <h6>Subtasks</h6>
            <ul className="proj-subtasks">
              {(task.subtasks || []).map((s) => (
                <li key={s.id}>
                  <input type="checkbox" checked={s.status === 'done'} readOnly />
                  <span className={s.status === 'done' ? 'done' : ''}>{s.title}</span>
                  <span className="proj-badge sm" >{STATUS_LABEL[s.status] || s.status}</span>
                </li>
              ))}
              {(task.subtasks || []).length === 0 && <li className="proj-muted">None.</li>}
            </ul>
            <div className="proj-add-task">
              <input className="proj-input" placeholder="Add subtask…" value={subtask}
                onChange={(e) => setSubtask(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addSubtask()} />
              <button className="proj-btn" onClick={addSubtask}>Add</button>
            </div>
          </div>

          {/* Resources */}
          <div className="proj-section">
            <h6>Resources</h6>
            <label className="proj-btn proj-btn-upload">{uploading ? 'Uploading…' : 'Upload file'}
              <input type="file" hidden onChange={uploadFile} disabled={uploading} />
            </label>
            <ul className="proj-resource-list">
              {(task.resources || []).map((r) => (
                <li key={r.id}>
                  <i className={`bi ${r.kind === 'file' ? 'bi-paperclip' : r.kind === 'path' ? 'bi-folder' : 'bi-link-45deg'}`} />
                  {r.kind === 'path'
                    ? <span title={r.path}>{r.title || r.path}</span>
                    : <button className="proj-link" onClick={() => (r.kind === 'file' ? download(r.id) : window.open(r.url, '_blank', 'noopener'))}>{r.title || r.file_name || r.url}</button>}
                  <button className="proj-icon-btn" onClick={() => projectsAPI.deleteResource(r.id).then(load)}>×</button>
                </li>
              ))}
              {(task.resources || []).length === 0 && <li className="proj-muted">None.</li>}
            </ul>
          </div>

          {/* Comments */}
          <div className="proj-section">
            <h6>Comments</h6>
            <div className="proj-comment-add">
              <textarea className="proj-input" rows={2} placeholder="Add a comment…" value={comment} onChange={(e) => setComment(e.target.value)} />
              <button className="proj-btn proj-btn-primary" onClick={addComment}>Post</button>
            </div>
            <ul className="proj-comment-list">
              {(task.comments || []).map((c) => (
                <li key={c.id}>
                  <div>
                    <div className="proj-comment-meta"><strong>{c.author_name || c.author_email}</strong> · {new Date(c.created_at).toLocaleString('en-AU')}</div>
                    <div className="proj-comment-body">{c.body}</div>
                  </div>
                </li>
              ))}
              {(task.comments || []).length === 0 && <li className="proj-muted">No comments.</li>}
            </ul>
          </div>

          <div className="proj-muted proj-task-foot">
            Assignee: {userName(task.assignee_id)} · Manager: {userName(task.manager_id)} · Created by {userName(task.created_by_id)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskModal;
